import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

import { API_BASE_URL } from "@/config";
import { CUSTOMER_ORG_HEADER, customerOrgFor } from "@/lib/actingFor";
import { useAuthStore } from "@/stores/authStore";

export const http = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  // SASE-35 (P35c): a partner's person managing a customer's SASE pages
  const customerOrg = customerOrgFor(config.url ?? "");
  if (customerOrg) {
    config.headers.set(CUSTOMER_ORG_HEADER, customerOrg);
  }
  return config;
});

// Auth endpoints whose own 401s must never trigger a token refresh (a failed
// login is a failed login; a failed refresh means the session is over).
const REFRESH_EXEMPT = [
  "/auth/login",
  "/auth/register",
  "/auth/refresh",
  "/auth/logout",
];

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

interface RefreshResponse {
  access_token: string;
  expires_in: number;
}

// Single-flight: concurrent 401s all await the same refresh call so the
// rotating refresh cookie is only spent once.
let refreshInFlight: Promise<string> | null = null;

async function requestRefresh(): Promise<string> {
  // Bare axios (not `http`): skips the interceptors so a 401 here cannot
  // recurse, and `withCredentials` lets the httpOnly refresh cookie flow.
  const { data } = await axios.post<RefreshResponse>(
    `${API_BASE_URL}/auth/refresh`,
    null,
    { withCredentials: true },
  );
  useAuthStore.getState().setAccessToken(data.access_token, data.expires_in);
  return data.access_token;
}

function refreshOnce(): Promise<string> {
  refreshInFlight ??= requestRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

function clearSessionAndRedirect(): void {
  useAuthStore.getState().clear();
  if (typeof window !== "undefined" && window.location.pathname !== "/login") {
    const next = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    window.location.replace(`/login?next=${next}`);
  }
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }
    const config = error.config as RetriableConfig | undefined;
    const url = config?.url ?? "";
    const exempt = REFRESH_EXEMPT.some((path) => url.includes(path));
    if (config && !exempt && !config._retried) {
      try {
        const token = await refreshOnce();
        config._retried = true;
        config.headers.set("Authorization", `Bearer ${token}`);
        return await http(config);
      } catch {
        // Refresh failed (no/expired cookie): fall through to logout below.
      }
    }
    clearSessionAndRedirect();
    return Promise.reject(error);
  },
);
